import React from 'react';
import { t, type MessageKey } from '@core/i18n';
import { formatDashboardTimestamp } from '../../utils/timestamps';
import type { DashboardBillingInvoice } from './consoleBillingApi';

export type BillingSubview =
  | { kind: 'account' }
  | { kind: 'invoices' }
  | { kind: 'invoice'; invoiceId: string };

export type BillingMetric = {
  label: string;
  value: string;
  hint: string;
  /* Optional data-driven color for the value (e.g. balance health). */
  tone?: 'success' | 'warning' | 'danger';
};

export function formatTimestamp(value: string | null): string {
  return formatDashboardTimestamp(value, '—');
}

export function parsePositiveInteger(value: string): number | null {
  const raw = String(value || '').trim();
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

export function parseBillingSubview(pathname: string): BillingSubview {
  const normalized = String(pathname || '').trim();
  if (
    normalized === '/dashboard/invoices' ||
    normalized === '/dashboard/invoices/' ||
    normalized === '/dashboard/billing/invoices' ||
    normalized === '/dashboard/billing/invoices/'
  ) {
    return { kind: 'invoices' };
  }
  const invoiceMatch = normalized.match(/^\/dashboard\/(?:billing\/invoices|invoices)\/([^/]+)$/);
  if (invoiceMatch?.[1]) {
    try {
      return { kind: 'invoice', invoiceId: decodeURIComponent(invoiceMatch[1]) };
    } catch {
      return { kind: 'invoice', invoiceId: invoiceMatch[1] };
    }
  }
  return { kind: 'account' };
}

export function isInvoiceOverdue(invoice: DashboardBillingInvoice): boolean {
  if (invoice.status !== 'OPEN' || !invoice.dueAt) return false;
  const dueAtMs = Date.parse(invoice.dueAt);
  if (Number.isNaN(dueAtMs)) return false;
  return dueAtMs < Date.now();
}

const INVOICE_STATUS_LABEL_KEYS: ReadonlyMap<string, MessageKey> = new Map<string, MessageKey>(
  Object.entries({
    action_required: 'billingStatusActionRequired',
    created: 'billingStatusCreated',
    pending: 'billingStatusPending',
    confirming: 'billingStatusConfirming',
    open: 'billingStatusOpen',
    past_due: 'billingStatusPastDue',
    paid: 'billingStatusPaid',
    settled: 'billingStatusSettled',
    partially_settled: 'billingStatusPartiallySettled',
    overpaid: 'billingStatusOverpaid',
    void: 'billingStatusVoid',
    canceled: 'billingStatusCanceled',
    expired: 'billingStatusExpired',
    uncollectible: 'billingStatusUncollectible',
    failed: 'billingStatusFailed',
    disputed: 'billingStatusDisputed',
  } satisfies Record<string, MessageKey>),
);

export function formatInvoiceStatusLabel(status: string): string {
  const normalized = String(status || '')
    .trim()
    .toUpperCase();
  if (!normalized) return t('billingStatusUnknown');
  const knownKey = INVOICE_STATUS_LABEL_KEYS.get(normalized.toLowerCase());
  if (knownKey) return t(knownKey);
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => `${part[0]}${part.slice(1).toLowerCase()}`)
    .join(' ');
}

/** Labels a billing document type (receipt, usage statement or monthly invoice). */
export function billingDocumentTypeLabel(type: DashboardBillingInvoice['documentType']): string {
  switch (type) {
    case 'PURCHASE_RECEIPT':
      return t('billingDocPurchaseReceipt');
    case 'USAGE_STATEMENT':
      return t('billingDocUsageStatement');
    case 'MONTHLY_INVOICE':
      return t('billingDocMonthlyInvoice');
  }
}

/** Fills each `{name}` in a message with a node, so a link can sit inside translated text. */
export function textWithNodes(
  text: string,
  nodes: Readonly<Record<string, React.ReactNode>>,
): React.ReactNode[] {
  return text.split(/(\{\w+\})/).map((part, index) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1];
    const node = name === undefined ? undefined : nodes[name];
    return <React.Fragment key={index}>{node === undefined ? part : node}</React.Fragment>;
  });
}

export function getInvoiceStatusBadgeClassName(status: string): string {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();
  if (
    normalized === 'action_required' ||
    normalized === 'created' ||
    normalized === 'pending' ||
    normalized === 'confirming' ||
    normalized === 'open' ||
    normalized === 'past_due'
  ) {
    return 'dashboard-data-table__status dashboard-data-table__status--warning';
  }
  if (
    normalized === 'paid' ||
    normalized === 'settled' ||
    normalized === 'partially_settled' ||
    normalized === 'overpaid'
  ) {
    return 'dashboard-data-table__status dashboard-data-table__status--success';
  }
  if (normalized === 'void' || normalized === 'canceled' || normalized === 'expired') {
    return 'dashboard-data-table__status dashboard-data-table__status--neutral';
  }
  if (normalized === 'uncollectible' || normalized === 'failed' || normalized === 'disputed') {
    return 'dashboard-data-table__status dashboard-data-table__status--danger';
  }
  return 'dashboard-data-table__status dashboard-data-table__status--neutral';
}

export function buildInvoicePdfFilename(
  invoice: DashboardBillingInvoice | null,
  invoiceId: string,
): string {
  const safeInvoiceId = String(invoice?.id || invoiceId || 'invoice').trim();
  const safePeriod = String(invoice?.periodMonthUtc || 'unknown')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '_');
  let prefix = 'statement';
  if (invoice?.documentType === 'PURCHASE_RECEIPT') prefix = 'receipt';
  else if (invoice?.documentType === 'MONTHLY_INVOICE') prefix = 'invoice';
  return `${prefix}_${safePeriod}_${safeInvoiceId}.pdf`;
}

export function BillingMetricsGrid(props: {
  metrics: BillingMetric[];
  ariaLabel: string;
}): React.JSX.Element {
  const { metrics, ariaLabel } = props;
  return (
    <section className="dashboard-kpi-grid dashboard-kpi-grid--content" aria-label={ariaLabel}>
      {metrics.map((metric) => (
        <article className="dashboard-kpi-card" key={metric.label}>
          <p className="dashboard-kpi-card__label">{metric.label}</p>
          <p
            className={`dashboard-kpi-card__value${
              metric.tone ? ` dashboard-kpi-card__value--${metric.tone}` : ''
            }`}
          >
            {metric.value}
          </p>
          <p className="dashboard-kpi-card__hint">{metric.hint}</p>
        </article>
      ))}
    </section>
  );
}

/** Marks billing state that every product of the organization shares. */
export function OrganizationSharedNote(props: {
  readonly serviceLabel: string;
  readonly subject: 'account' | 'invoices';
}): React.JSX.Element {
  return (
    <div className="dashboard-shared-scope-note" role="note">
      <span className="dashboard-shared-scope-note__badge">{t('billingOrganizationWide')}</span>
      <p>
        {props.subject === 'account'
          ? t('billingSharedNoteAccount', { service: props.serviceLabel })
          : t('billingSharedNoteInvoices', { service: props.serviceLabel })}
      </p>
    </div>
  );
}
